import { useState, useEffect, useCallback } from 'react';
import { AlertCircle, PlusCircle, Sun, Battery, DollarSign, Leaf } from 'lucide-react';
import type { UserProfile, Project } from '../types';
import { supabase } from '../lib/supabase';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { SubscriptionSelector } from './SubscriptionSelector';
import { YourProjects } from './YourProjects';

interface ProfileDashboardProps {
  profile: UserProfile;
  onNewProject: () => void;
}

export function ProfileDashboard({ profile, onNewProject }: ProfileDashboardProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [dashboardMetrics, setDashboardMetrics] = useState({
    totalCapacity: 0,
    annualProduction: 0,
    totalInvestment: 0,
    co2Reduction: 0
  });

  const { checkProjectLimit, getCurrentPlan } = useSubscriptionStore();

  const calculateDashboardMetrics = useCallback((projectsList: Project[]) => {
    const metrics = projectsList.reduce((acc, project) => {
      // Total Capacity (kW)
      const systemSize = project.design?.systemSize || 0;
      acc.totalCapacity += systemSize;

      // Annual Production (kWh)
      const yearlyProduction = project.design?.yearlyProduction || 0;
      acc.annualProduction += yearlyProduction;

      // Total Investment (SAR)
      const totalCost = project.financials?.totalCost || 0;
      acc.totalInvestment += totalCost;

      // CO₂ Reduction (kg/year) - calculated as 0.7kg per kWh
      acc.co2Reduction += yearlyProduction * 0.7;

      return acc;
    }, {
      totalCapacity: 0,
      annualProduction: 0,
      totalInvestment: 0,
      co2Reduction: 0
    });

    setDashboardMetrics(metrics);
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedProjects: Project[] = data.map(p => ({
        id: p.id,
        status: p.project_status,
        type: p.user_type,
        design: p.solar_design,
        location: p.location_data,
        financials: p.solar_design?.financials,
        createdAt: p.created_at,
        siteVisit: p.site_visit
      }));

      calculateDashboardMetrics(transformedProjects);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch projects');
    } finally {
      setIsLoading(false);
    }
  }, [profile.id, calculateDashboardMetrics]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleNewProject = async () => {
    const canCreate = await checkProjectLimit(profile.id);
    if (!canCreate) {
      const currentPlan = getCurrentPlan();
      setError(`Your ${currentPlan?.tier} plan allows only ${currentPlan?.maxProjects} projects`);
      setShowUpgrade(true);
      return;
    }
    onNewProject();
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount == null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 text-center">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center text-red-700">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {showUpgrade ? (
        <SubscriptionSelector 
          onSelected={async (tier) => {
            const success = await useSubscriptionStore.getState().upgradeTier(tier as any);
            if (success) {
              setShowUpgrade(false);
              setError(null);
              onNewProject();
            }
          }}
          showUpgradeOnly={true}
        />
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8 border border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-0">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  Welcome back, {profile.name}!
                </h1>
                <p className="text-gray-600">
                  Manage your energy projects and track your savings.
                </p>
              </div>
              <button
                onClick={handleNewProject}
                className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-2 sm:py-3 px-4 sm:px-6 rounded-lg hover:from-blue-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform transition-all hover:scale-[1.02] active:scale-[0.98] font-medium shadow-lg w-full sm:w-auto"
              >
                <div className="flex items-center justify-center">
                  <PlusCircle className="h-5 w-5 mr-2" />
                  Start New Project
                </div>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-8">
              <div className="bg-blue-50 rounded-xl p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <Sun className="h-6 w-6 text-blue-600" />
                  <span className="text-blue-600 font-medium">Total Capacity</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">
                  {dashboardMetrics.totalCapacity.toFixed(1)} kW
                </div>
              </div>
              
              <div className="bg-green-50 rounded-xl p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <Battery className="h-6 w-6 text-green-600" />
                  <span className="text-green-600 font-medium">Annual Production</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">
                  {Math.round(dashboardMetrics.annualProduction).toLocaleString()} kWh
                </div>
              </div>
              
              <div className="bg-purple-50 rounded-xl p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="h-6 w-6 text-purple-600" />
                  <span className="text-purple-600 font-medium">Total Investment</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">
                  {formatCurrency(dashboardMetrics.totalInvestment)}
                </div>
              </div>
              
              <div className="bg-amber-50 rounded-xl p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <Leaf className="h-6 w-6 text-amber-600" />
                  <span className="text-amber-600 font-medium">CO₂ Reduction</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">
                  {Math.round(dashboardMetrics.co2Reduction)} kg/year
                </div>
              </div>
            </div>
          </div>

          <YourProjects user={profile} />
        </>
      )}
    </div>
  );
}
